import ErrorUtil from '../util/ErrorUtil';
import Service from './Service';

/**
 * Classe utilizada para tratar regras de negócio e manipular o retorno dos webservices
 */
class ProcessoService extends Service {
    recuperarDetalhesProcessoSessao(numeroProcesso, idSessaoJulgamento) {
        return this.getProcessoApi().recuperarDetalhesProcessoSessao(numeroProcesso, idSessaoJulgamento);
    }

    recuperarDetalhesProcesso(numeroProcesso, origem, tratamentoErroComponente) {
        return this.getProcessoApi().recuperarDetalhesProcesso(numeroProcesso, origem, tratamentoErroComponente);
    }

    recuperarEmentaDecisao(numeroProcesso, idSessaoJulgamento) {
        return this.getProcessoApi().recuperarEmentaDecisao(numeroProcesso, idSessaoJulgamento);
    }

    recuperarPlanilha(numeroCompleto) {
        return this.getProcessoApi().recuperarPlanilha(numeroCompleto);
    }

    recuperarConteudoVoto(numeroProcesso, idSessaoJulgamento) {
        return this.getProcessoApi().recuperarConteudoVoto(numeroProcesso, idSessaoJulgamento);
    }

    recuperarEditorVoto(numeroProcesso, idSessaoJulgamento) {
        return this.getProcessoApi().recuperarEditorVoto(numeroProcesso, idSessaoJulgamento);
    }

    recuperarConteudoAcordaoAssinado(numeroProcesso, idSessaoJulgamento) {
        return this.getProcessoApi().recuperarConteudoAcordaoAssinado(numeroProcesso, idSessaoJulgamento);
    }

    recuperarPreVotacao(idProcesso) {
        return this.getProcessoApi().recuperarPreVotacao(idProcesso);
    }

    recuperarVotacaoMagistrado(idProcesso, siglaMagistrado) {
        return this.getProcessoApi().recuperarVotacaoMagistrado(idProcesso, siglaMagistrado);
    }

    salvarVotoMagistrado(idProcesso, siglaMagistrado, voto) {
        return this.getProcessoApi().salvarVotoMagistrado(idProcesso, siglaMagistrado, voto);
    }

    async recuperarTiposResultadosJulgamento() {
        return this.getProcessoApi().recuperarTiposResultadosJulgamento();
    }

    recuperarDecisoesProcessuais() {
        return this.getProcessoApi().recuperarDecisoesProcessuais();
    }

    recuperarMovimentosProcessuais() {
        return this.getProcessoApi().recuperarMovimentosProcessuais();
    }

    async bloquearBaixa(idSessao, registro) {
        try {
            const result = registro.bloqueioBaixa
                ? await this.getProcessoApi().desbloquearBaixa(registro.sessaoId, registro.processo.numeroCompleto)
                : await this.getProcessoApi().bloquearBaixa(registro.sessaoId, registro.processo.numeroCompleto);

            if (result.status >= 200 && result.status < 400) {
                return !registro.bloqueioBaixa;
            }
            console.error('Erro ao alterar o bloqueio de baixa', result);
            return registro.bloqueioBaixa;
        } catch (error) {
            console.error(error);
        }

        return registro.bloqueioBaixa;
    }

    async recuperarInfoTranscendenciaBloqueioBaixa(idSessao, registro) {
        return this.getProcessoApi().recuperarInfoTranscendenciaBloqueioBaixa(
            registro.sessaoId,
            registro.processo.numeroCompleto
        );
    }

    async marcarTranscendencia(idSessao, registro) {
        try {
            const result = registro.transcendencia
                ? await this.getProcessoApi().desmarcarTranscendencia(
                      registro.sessaoId,
                      registro.processo.numeroCompleto
                  )
                : await this.getProcessoApi().marcarTranscendencia(registro.sessaoId, registro.processo.numeroCompleto);

            if (result.status >= 200 && result.status < 400) {
                return !registro.transcendencia;
            }
            console.error('Erro ao alterar a transcendência', result);
            return registro.transcendencia;
        } catch (error) {
            console.error(error);
        }

        return registro.transcendencia;
    }

    async registrarJulgamento(processos, idSessao, tipoResultadoJulgamento, vistas, tratamentoErroComponente) {
        let resultadoProcessamento = [];

        try {
            const response = await this.getProcessoApi().registrarJulgamento(
                this.getNumerosProcessos(processos),
                idSessao,
                tipoResultadoJulgamento,
                tratamentoErroComponente,
                vistas
            );

            if (this.isRespostaSucesso(response)) {
                if (response.data.sequenciaisApregoamento) {
                    resultadoProcessamento = resultadoProcessamento.concat(
                        this.getResultadoProcessamentoApreciar(response.data)
                    );
                }

                if (response.data.erros) {
                    resultadoProcessamento = resultadoProcessamento.concat(
                        this.getErrosProcessamentoApreciar(response.data)
                    );
                }
            } else {
                resultadoProcessamento = processos.map((registro) => {
                    return ErrorUtil.getResultadoProcessamentoErroServico(response, registro.processo.numeroCompleto);
                });
            }
        } catch (error) {
            resultadoProcessamento = processos.map((registro) => {
                return ErrorUtil.getResultadoProcessamentoErro(error, registro.processo.numeroCompleto);
            });
        }

        return resultadoProcessamento;
    }

    getResultadoProcessamentoApreciar(response) {
        return Object.entries(response.sequenciaisApregoamento).map(([numeroProcesso, value]) => ({
            numeroProcesso: numeroProcesso,
            sequencialApregoamento: value,
            dataJulgamento: response.dataJulgamento
        }));
    }

    getErrosProcessamentoApreciar(response) {
        return response.erros.map((erro) => ({
            numeroProcesso: erro,
            erro: true,
            mensagem: 'Não foi possível apreciar o processo'
        }));
    }

    getNumerosProcessos(processos) {
        return processos.map((registro) => registro.processo.numeroCompleto);
    }

    async salvarVistaRegimental(vistas, processos) {
        for (const index in processos) {
            const registro = processos[index];
            await this.getProcessoApi().salvarVistaRegimental(
                vistas,
                registro.sessaoId,
                registro.processo.numeroCompleto
            );
        }
    }

    async removerVistaRegimental(registro) {
        await this.getProcessoApi().removerVistaRegimental(registro.sessaoId, registro.processo.numeroCompleto);
    }

    async gravarDecisao(idSessao, numeroProcesso, idDecisao) {
        if (idDecisao > 0) {
            await this.getProcessoApi().gravarDecisao(idSessao, numeroProcesso, idDecisao);
        } else {
            await this.getProcessoApi().excluirDecisao(idSessao, numeroProcesso);
        }
    }

    async gravarDecisaoLote(registro, idDecisao) {
        return this.processarOperacaoLote(
            () => this.getProcessoApi().gravarDecisaoLote(registro.sessaoId, registro.numeroProcesso, idDecisao),
            registro
        );
    }

    async gravarComposicaoProcesso(sessaoId, numeroCompleto, composicao) {
        const retorno = await this.getProcessoApi().gravarComposicaoProcesso(sessaoId, numeroCompleto, composicao);
        //await this.getSessaoApi().notificarAlterarProcesso(sessaoId, numeroCompleto);
        return retorno;
    }

    async gravarComposicaoLote(registro, composicao, manterImpedimentosComposicao) {
        return this.processarOperacaoLote(
            () =>
                this.getProcessoApi().gravarComposicaoLote(
                    registro.sessaoId,
                    [registro.processo.numeroCompleto],
                    composicao,
                    manterImpedimentosComposicao
                ),
            registro
        );
    }

    async gravarProcuradorLote(registro, procurador) {
        return this.processarOperacaoLote(
            () =>
                this.getProcessoApi().gravarProcuradorLote(
                    registro.sessaoId,
                    [registro.processo.numeroCompleto],
                    procurador
                ),
            registro
        );
    }

    recuperarDecisao(idSessao, numeroProcesso) {
        return this.getProcessoApi().recuperarDecisao(idSessao, numeroProcesso);
    }

    /**
     * O serviço apaga todos os movimentos e grava o que foi passado, sendo necessário enviar todos os movimentos com complementos.
     *
     * @param {*} idSessao
     * @param {*} numeroProcesso
     * @param {*} movimentos
     */
    async gravarMovimentos(idSessao, numeroProcesso, movimentos) {
        await this.getProcessoApi().gravarMovimentos(idSessao, numeroProcesso, movimentos);
        this.getSessaoApi().notificarAlterarProcesso(idSessao, numeroProcesso);
    }

    async gravarMovimentosLote(registro, movimentos) {
        return this.processarOperacaoLote(
            () =>
                this.getProcessoApi().gravarMovimentosLote(
                    registro.sessaoId,
                    registro.processo.numeroCompleto,
                    movimentos
                ),
            registro
        );
    }

    async editarNomeAdvogadoSustentacaoOral(idSessao, numeroProcesso, nomeAdvogadoAnterior, nomeAdvogadoNovo) {
        await this.getProcessoApi().editarNomeAdvogadoSustentacaoOral(
            idSessao,
            numeroProcesso,
            nomeAdvogadoAnterior,
            nomeAdvogadoNovo
        );
    }

    incluirAdvogadoSustentacaoOral(idSessao, numeroProcesso, nomeAdvogadoNovo, codigoParte) {
        this.getProcessoApi().incluirAdvogadoSustentacaoOral(idSessao, numeroProcesso, nomeAdvogadoNovo, codigoParte);
    }

    recuperarMovimentosProcesso(idSessao, numeroProcesso) {
        return this.getProcessoApi().recuperarMovimentosProcesso(idSessao, numeroProcesso);
    }

    recuperarMovimentosGabinete(idSessao, numeroProcesso) {
        return this.getProcessoApi().recuperarMovimentosGabinete(idSessao, numeroProcesso);
    }

    recuperarConteudoDocumento(idSessao, numeroProcesso, tipoArquivo, idDocumento) {
        return this.getProcessoApi().recuperarConteudoDocumento(idSessao, numeroProcesso, tipoArquivo, idDocumento);
    }

    async registrarPresencaAdvogadoProcesso(idSessao, numeroProcesso, advogado, notificarClientes = true) {
        const resposta = await this.getProcessoApi().registrarPresencaAdvogadoProcesso(
            idSessao,
            numeroProcesso,
            advogado,
            notificarClientes
        );

        return resposta;
    }

    async recuperarAlertasImpedimento(idSessao, numeroProcesso) {
        return this.getProcessoApi().recuperarAlertasImpedimento(idSessao, numeroProcesso);
    }

    async recuperarObservacaoVoto(idSessao, numeroProcesso, tipoObservacao, siglaMagistrado) {
        return this.getProcessoApi().recuperarObservacaoVoto(idSessao, numeroProcesso, tipoObservacao, siglaMagistrado);
    }

    async recuperarObservacaoVotoPeloId(idSessao, numeroProcesso, tipoObservacao, siglaMagistrado, idObservacao) {
        return this.getProcessoApi().recuperarObservacaoVotoPeloId(
            idSessao,
            numeroProcesso,
            tipoObservacao,
            siglaMagistrado,
            idObservacao
        );
    }

    async recuperarConteudoObservacaoVotoPeloId(idSessao, numeroProcesso, tipoObservacao, siglaMagistrado, idConteudo) {
        return this.getProcessoApi().recuperarConteudoObservacaoVotoPeloId(
            idSessao,
            numeroProcesso,
            tipoObservacao,
            siglaMagistrado,
            idConteudo
        );
    }

    async alterarTipoJulgamento(registro, tipoJulgamento) {
        return registro.sessaoId
            ? this.getProcessoApi().alterarTipoJulgamento(
                  registro.sessaoId,
                  registro.processo.numeroCompleto,
                  tipoJulgamento
              )
            : this.getProcessoApi().alterarIndicacaoTipoJulgamento(
                  registro.processo.origem,
                  registro.processo.numeroCompleto,
                  tipoJulgamento
              );
    }

    async alterarTipoJulgamentoLote(registro, tipoJulgamento) {
        return this.processarOperacaoLote(
            () =>
                this.getProcessoApi().alterarTipoJulgamentoLote(
                    registro.sessaoId,
                    [registro.processo.numeroCompleto],
                    tipoJulgamento
                ),
            registro
        );
    }

    async remeterParaPresencial(registro, motivo) {
        return this.getProcessoApi().remeterParaPresencial(registro.sessaoId, registro.processo.numeroCompleto, motivo);
    }

    async remeterParaSessaoFutura(sessaoId, numeroProcesso, tipo, motivo) {
        return this.getProcessoApi().remeterParaSessaoFutura(sessaoId, numeroProcesso, tipo, motivo);
    }

    async recuperarMotivoRemessaPresencial(registro) {
        return this.getProcessoApi().recuperarMotivoRemessaPresencial(
            registro.sessaoId,
            registro.processo.numeroCompleto
        );
    }

    async incluirProcessoEmPauta(idPauta, registro, preparacaoPauta) {
        return this.getProcessoApi().incluirProcessoEmPauta(
            idPauta,
            registro.processo.numeroCompleto,
            registro.tipoInclusao,
            registro.tipoJulgamento,
            preparacaoPauta,
            false
        );
    }

    async incluirProcessoEmPautaLote(idPauta, registro) {
        return this.processarOperacaoLote(
            () =>
                this.getProcessoApi().incluirProcessoEmPauta(
                    idPauta,
                    registro.processo.numeroCompleto,
                    registro.tipoInclusao,
                    registro.tipoJulgamento,
                    true,
                    true
                ),
            registro
        );
    }

    async removerProcessoPauta(idPauta, registro) {
        return this.getProcessoApi().removerProcessoPauta(idPauta, registro.processo.numeroCompleto, false);
    }

    async removerProcessoPautaEmLote(idPauta, registro) {
        return this.processarOperacaoLote(
            () => this.getProcessoApi().removerProcessoPauta(idPauta, registro.processo.numeroCompleto, true),
            registro
        );
    }

    async recuperarProcessoPautavel(idPauta, numeroCompleto, tratamentoErroComponente) {
        return this.getProcessoApi().recuperarProcessoPautavel(idPauta, numeroCompleto, tratamentoErroComponente);
    }

    async liberarProcessoGabinete(registro) {
        return this.processarOperacaoLote(
            () => this.getProcessoApi().liberarProcessoGabinete(registro.sessaoId, registro.processo.numeroCompleto),
            registro
        );
    }

    async recuperarUrlEdicaoVoto(registro) {
        return this.getProcessoApi().recuperarUrlEdicaoVoto(
            registro.sessaoId,
            registro.numeroProcesso,
            registro.informacoesAdicionais.identificadorEdicaoVoto
        );
    }

    async recuperarJulgamentosAnteriores(numeroCompleto, origem) {
        return this.getProcessoApi().recuperarJulgamentosAnteriores(numeroCompleto, origem);
    }

    async alterarPermissaoSustentacao(sessaoId, numeroProcesso, permiteSustentacao, permiteDestaque) {
        return this.getProcessoApi().alterarPermissaoSustentacao(
            sessaoId,
            numeroProcesso,
            permiteSustentacao,
            permiteDestaque
        );
    }

    async ajustarVoto(registro) {
        return this.getProcessoApi().ajustarVoto(registro.sessaoId, registro.processo.numeroCompleto);
    }
}

export default ProcessoService;
